'use client';

import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useBoolean } from 'minimal-shared/hooks';
import { zodResolver } from '@hookform/resolvers/zod';

import { Box, Button, CardHeader, Typography } from '@mui/material';

import { notify } from 'src/utils/helper';
import { USER_ID_STORAGE_KEY } from 'src/utils/constants';
import { ConfirmDialog } from 'src/utils/common/confirm-dialog';
// import { updateContactPersonSchema_ORDER } from 'src/utils/validations';

import { useLocale, useTranslate } from 'src/locales';
import { useOrdersDetails } from 'src/api/useOrders';
import { usePostOrderContactDetailsAndNotes } from 'src/api/Order/useOrder';

import { UploadBox } from 'src/components/upload';
import { Form, Field } from 'src/components/hook-form';

import CustomDrawer from 'src/sections/_home/dashboard/CustomDrawer';
import { createSchemas } from '../use-validations';

// import { useAuthContext } from 'src/auth/hooks';

function UpdateContactDetailsModal({
  orderId,
  successMessage,
  handleClose,
  open,
  setIsUpdateContactDetailsModalVisible,
  setShowBookNowForm,
}) {
  // const { t } = useTranslate();
  const { t } = useLocale();

  // const { user } = useAuthContext();
  const [file, setFile] = useState(null);
  const confirmDialog = useBoolean(false);
  const { data, isLoading } = useOrdersDetails(orderId);
  const loggedInmobileNumber = localStorage.getItem('mobileNumber');
  const userID = sessionStorage.getItem(USER_ID_STORAGE_KEY);

  const orderDetails = data?.order[0];

  const {
    data: postData,
    mutateAsync: postDetails,
    isPending,
  } = usePostOrderContactDetailsAndNotes();

  const defaultValues = {
    pickupLocation: '',
    deliveryLocation1: '',
    deliveryLocation2: '',
    note: '',
    invoice: '',
    isEdit: false,
  };

  const { updateContactPersonSchema_ORDER } = createSchemas();

  const methods = useForm({
    resolver: zodResolver(updateContactPersonSchema_ORDER),
    defaultValues,
    mode: 'all',
  });
  const { handleSubmit, reset } = methods;

  const onSubmit = handleSubmit(async (values) => {
    try {
      var mobileNumber = {
        pickupLocation: values.pickupLocation,
        deliveryLocation1: values.deliveryLocation1,
      };

      if (values.deliveryLocation2) {
        mobileNumber['deliveryLocation2'] = values.deliveryLocation2;
      }

      const payload = {
        mobileNumber,
        invoice: file,
        invoiceNumber: values?.note,
        userID,
        orderID: orderId,
      };

      postDetails(payload);
    } catch (error) {
      console.log('error : ', error);
    }
  });

  const onClose = () => {
    confirmDialog.onTrue();
  };

  useEffect(() => {
    if (postData?.success) {
      notify('success', postData?.success?.message || 'Success');
      handleClose?.();
      reset?.();
      setFile(null);
      setShowBookNowForm(false);
      sessionStorage.clear();
    }
    if (postData?.error) {
      notify('error', postData?.error?.message || postData?.error || 'Error');
    }
  }, [postData, reset]);

  const renderConfirmDialog = () => (
    <ConfirmDialog
      open={confirmDialog.value}
      onClose={() => {}}
      // title="Delete"
      disableEscapeKeyDown
      sx={{ zIndex: 1600 }}
      content={
        <>
          {t('Mobile_numbers_which_are_not_enterted_will_be_sent_to')}{' '}
          <strong> {loggedInmobileNumber} </strong>
        </>
      }
      action={
        <Box sx={{ display: 'flex', gap: 3 }}>
          <Button
            variant="outlined"
            color="#000"
            onClick={() => {
              confirmDialog.onFalse();
              setIsUpdateContactDetailsModalVisible(true);
            }}
          >
            {t('Edit')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              confirmDialog.onFalse();
              setShowBookNowForm(false);
              setIsUpdateContactDetailsModalVisible(false);
              sessionStorage.clear();
              reset();
              setFile(null);
            }}
          >
            {t('ok')}
          </Button>
        </Box>
      }
      customSubmitButtons
    />
  );

  return (
    <>
      <CustomDrawer
        open={open}
        width={400}
        title={t('Order_Placed')}
        handleback={onClose}
        onClose={onClose}
      >
        <Box flexDirection="column" display="flex" gap={2}>
          <Typography sx={{ mb: 4 }}>{successMessage}</Typography>

          <CardHeader sx={{ p: 0 }} title={t('Add_contact_number_of_responsible_persons')} />
          <Form methods={methods} onSubmit={onSubmit}>
            <Box
              sx={{
                gap: 2,
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                alignItems: 'center',
              }}
            >
              <Field.Text
                fullWidth
                name="pickupLocation"
                variant="outlined"
                label={t('contact_number_at_pickup')}
                disabled={isPending}
              />

              <Field.Text
                fullWidth
                name="deliveryLocation1"
                variant="outlined"
                label={t('contact_number_at_delivery') + ' - 1'}
                disabled={isPending}
              />
              {orderDetails?.targetLocation?.[1]?.mobileNumber && (
                <Field.Text
                  fullWidth
                  name="deliveryLocation2"
                  variant="outlined"
                  label={t('contact_number_at_delivery') + ' - 2'}
                  disabled={isPending}
                />
              )}

              <Box display="flex" flexDirection="row" gap={2}>
                <Field.Text
                  multiline
                  rows={2}
                  name="note"
                  label={t('Notes')}
                  disabled={isPending}
                />

                <UploadBox
                  value={file}
                  onDrop={(accepted) => {
                    if (accepted?.length) setFile(accepted[0]);
                  }}
                  accept={{ 'application/pdf': [], 'image/*': [] }}
                  sx={{
                    width: { xs: 80, md: 100 },
                    height: { xs: 80, md: 80 },
                  }}
                />
              </Box>

              <Box display="flex" flexDirection="row" gap={2} width="100%">
                <Button
                  variant="outlined"
                  color="error"
                  fullWidth
                  size="large"
                  onClick={() => {
                    confirmDialog.onTrue();
                  }}
                >
                  {t('skip')}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  loading={isPending}
                  size="large"
                >
                  {t('Save')}
                </Button>
              </Box>
            </Box>
          </Form>
        </Box>
      </CustomDrawer>
      {renderConfirmDialog()}
    </>
  );
}

export default UpdateContactDetailsModal;
